import WS from 'wavesurfer.js';

import { IMedia } from './Media';
import Observable from './Observable';

/**
 * @deprecated
 */
class WaveSurfer extends Observable implements IMedia {
  static EVENT = {
    AUDIO_PROCESS: 'audioprocess' as const,
    DESTROY: 'destroy' as const,
    ERROR: 'error' as const,
    FINISH: 'finish' as const,
    LOADING: 'loading' as const,
    MUTE: 'mute' as const,
    PAUSE: 'pause' as const,
    PLAY: 'play' as const,
    READY: 'ready' as const,
    SEEK: 'seek' as const,
    VOLUME: 'volume' as const,
  };

  private readonly id: string;

  private readonly element: HTMLElement;

  private loopEnabled: boolean = false;

  private audio: WS;

  private url: string | null = null;

  constructor(id: string) {
    super();

    this.id = id;
    this.element = document.createElement('div');
    this.element.setAttribute('id', this.id);
    this.element.style.width = '22rem';
    this.element.style.height = '5.4rem';

    const bufferElement = this.getBufferElement();
    bufferElement.appendChild(this.element);

    this.audio = WS.create({
      container: this.element,
      backend: 'WebAudio',
      removeMediaElementOnDestroy: true,
      scrollParent: true,
      hideScrollbar: true,
      waveColor: '#FD6874',
      progressColor: '#FA96A4',
      cursorColor: 'transparent',
      fillParent: true,
      minPxPerSec: 100,
      height: 54,
    });
    this.addListeners();
  }

  public load = (url: string): this => {
    this.clear();
    this.url = url;
    this.audio.load(url);
    this.audio.stop();
    return this;
  };

  public play = (): this => {
    if (!this.url) {
      return this;
    }
    this.audio.play()?.catch((error) => {
      throw error;
    });
    return this;
  };

  public pause = (): this => {
    if (!this.url) {
      return this;
    }
    this.audio.pause();
    return this;
  };

  public stop = (): this => {
    if (!this.url) {
      return this;
    }
    this.audio.stop();
    return this;
  };

  public mute = (): this => {
    this.audio.setMute(true);
    return this;
  };

  public unMute = (): this => {
    this.audio.setMute(false);
    return this;
  };

  public volume = (value: number): this => {
    this.audio.setVolume(value);
    return this;
  };

  public loop = (value: boolean): this => {
    this.loopEnabled = value;
    if (value) {
      this.audio.on(WaveSurfer.EVENT.FINISH, this.handleRestart);
    }
    if (!value) {
      this.audio.un(WaveSurfer.EVENT.FINISH, this.handleRestart);
    }
    return this;
  };

  public getElement = (): HTMLElement => {
    return this.element;
  };

  public getVolume = (): number => {
    return this.audio.getVolume();
  };

  public getDuration = (): number => {
    return this.audio.getDuration();
  };

  public getCurrentTime = (): number => {
    return this.audio.getCurrentTime();
  };

  public isMute = (): boolean => {
    return this.audio.getMute();
  };

  public isPlaying = (): boolean => {
    return this.audio.isPlaying();
  };

  public isLooping = (): boolean => {
    return this.loopEnabled;
  };

  public destroy = (): void => {
    this.removeAllListeners();
    this.removeListeners();
    this.clear();
  };

  private addListeners = (): void => {
    this.audio.on(WaveSurfer.EVENT.AUDIO_PROCESS, (payload) => this.handleEvent({ type: WaveSurfer.EVENT.AUDIO_PROCESS, payload }));
    this.audio.on(WaveSurfer.EVENT.DESTROY, (payload) => this.handleEvent({ type: WaveSurfer.EVENT.DESTROY, payload }));
    this.audio.on(WaveSurfer.EVENT.ERROR, (payload) => this.handleEvent({ type: WaveSurfer.EVENT.ERROR, payload }));
    this.audio.on(WaveSurfer.EVENT.FINISH, (payload) => this.handleEvent({ type: WaveSurfer.EVENT.FINISH, payload }));
    this.audio.on(WaveSurfer.EVENT.LOADING, (payload) => this.handleEvent({ type: WaveSurfer.EVENT.LOADING, payload }));
    this.audio.on(WaveSurfer.EVENT.MUTE, (payload) => this.handleEvent({ type: WaveSurfer.EVENT.MUTE, payload }));
    this.audio.on(WaveSurfer.EVENT.PAUSE, (payload) => this.handleEvent({ type: WaveSurfer.EVENT.PAUSE, payload }));
    this.audio.on(WaveSurfer.EVENT.PLAY, (payload) => this.handleEvent({ type: WaveSurfer.EVENT.PLAY, payload }));
    this.audio.on(WaveSurfer.EVENT.READY, (payload) => this.handleEvent({ type: WaveSurfer.EVENT.READY, payload }));
    this.audio.on(WaveSurfer.EVENT.SEEK, (payload) => this.handleEvent({ type: WaveSurfer.EVENT.SEEK, payload }));
    this.audio.on(WaveSurfer.EVENT.VOLUME, (payload) => this.handleEvent({ type: WaveSurfer.EVENT.VOLUME, payload }));
  };

  private removeListeners = (): void => {
    this.audio.unAll();
  };

  private clear = (): void => {
    this.audio.empty();
    this.url = null;
  };

  private handleRestart = (): void => {
    this.audio.play();
  };

  private handleEvent = (event: any): void => {
    const { type, payload } = event;
    this.dispatchEvent(type, payload);
  };

  private getBufferElement = () => {
    let element = (document.body.querySelector('[data-type="wave-surfer-buffer-container"]') as HTMLDivElement);
    if (!element) {
      element = (document.createElement('div') as HTMLDivElement);
      element.setAttribute('data-type', 'wave-surfer-buffer-container');
      element.style.position = 'absolute';
      element.style.top = '0';
      element.style.left = '-100%';
      element.style.pointerEvents = 'none';
      element.style.visibility = 'hidden';
    }
    if (typeof document !== 'undefined') {
      document.body.appendChild(element);
    }
    return element;
  };
}

export default WaveSurfer;
